import {useEffect, useState} from 'react';
import {Grid, MenuItem} from '@mui/material';
import {ExpansionPanel, InputField, CurrencySelect, MoneyField, NumberField} from '@/components/common';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import type {CompanyAnnual} from '@/services/accountService/companyAnnualService';
import {currencySymbolOf} from '@/components/common/currencyData';

type Props = {
    annualId: number;
    onChange?: (patch: Partial<CompanyAnnual>) => void;
    initialData?: {
        allotmentOfShares?: number;
        noOfShares?: number;
        pricePerShare?: number;
        priceCurrency?: string | null;
    } | null;
};

export default function EpAllotmentOfShares({annualId, onChange, initialData}: Props) {
    const store = useCompanyAnnualStore();
    const [local, setLocal] = useState<{
        allotmentOfShares: number;
        noOfShares: number;
        pricePerShare: number;
        allotmentAmount: number;
        priceCurrency: string | null
    }>({
        allotmentOfShares: 0,
        noOfShares: 0,
        pricePerShare: 0,
        allotmentAmount: 0,
        priceCurrency: 'HKD',
    });

    useEffect(() => {
        if (initialData) {
            const qty = Number(initialData.noOfShares ?? 0);
            const price = Number(initialData.pricePerShare ?? 0);
            setLocal({
                allotmentOfShares: Number(initialData.allotmentOfShares ?? 0),
                noOfShares: qty,
                pricePerShare: price,
                allotmentAmount: qty * price,
                priceCurrency: initialData.priceCurrency || 'HKD',
            });
            return;
        }

        let mounted = true;
        (async () => {
            const v = await store.getAllotmentOfSharesInfo(annualId);
            if (!mounted) return;
            setLocal({
                allotmentOfShares: Number(v.allotmentOfShares ?? 0),
                noOfShares: Number(v.noOfShares ?? 0),
                pricePerShare: Number(v.pricePerShare ?? 0),
                allotmentAmount: Number(v.allotmentAmount ?? 0),
                priceCurrency: v.priceCurrency || 'HKD',
            });
        })();
        return () => {
            mounted = false;
        };
    }, [annualId, store, initialData]);

    const emit = (patch: Partial<typeof local>) => {
        const next = {...local, ...patch};
        // auto compute allotmentAmount when source fields change
        if ('noOfShares' in patch || 'pricePerShare' in patch) {
            const qty = Number((patch.noOfShares ?? next.noOfShares) || 0);
            const price = Number((patch.pricePerShare ?? next.pricePerShare) || 0);
            next.allotmentAmount = qty * price;
        }
        setLocal(next);
        onChange?.(patch as Partial<CompanyAnnual>);
    };

    return (
        <ExpansionPanel title="Allotment of Shares (增資)">
            <Grid container spacing={0.5}>
                <Grid size={12 as any}>
                    <InputField select fullWidth variant="outlined" align="left"
                                label="Allotment of shares? (是否增資?)" value={local.allotmentOfShares ?? ''}
                                onChange={(e) => emit({allotmentOfShares: Number(e.target.value) as any})}>
                        <MenuItem dense value="">Select</MenuItem>
                        <MenuItem dense value={0}>No</MenuItem>
                        <MenuItem dense value={1}>Yes</MenuItem>
                    </InputField>
                </Grid>
                {local.allotmentOfShares === 1 && (
                    <>
                        <Grid size={6 as any}>
                            <NumberField
                                label="No. of Shares (股份數量)"
                                value={local.noOfShares}
                                type="integer"
                                editable
                                align="left"
                                onChangeNumber={(n) => emit({noOfShares: Number(n || 0)})}
                            />
                        </Grid>
                        <Grid size={6 as any}>
                            <MoneyField
                                label="Price per share (每股價格)"
                                value={local.pricePerShare}
                                showCents
                                editable
                                align="left"
                                currencySymbol={currencySymbolOf(local.priceCurrency || 'HKD')}
                                onChangeNumber={(n) => emit({pricePerShare: Number(n || 0)})}
                            />
                        </Grid>
                        <Grid size={6 as any}>
                            <MoneyField
                                label="Allotment Amount (增資金額)"
                                value={local.allotmentAmount}
                                showCents
                                editable={false}
                                align="left"
                                currencySymbol={currencySymbolOf(local.priceCurrency || 'HKD')}
                            />
                        </Grid>
                        <Grid size={6 as any}>
                            <CurrencySelect variant="outlined" label="Price Currency (貨幣)"
                                            value={local.priceCurrency || 'HKD'}
                                            onChange={(code: string) => emit({priceCurrency: code})}/>
                        </Grid>
                    </>
                )}
            </Grid>
        </ExpansionPanel>
    );
}
